// Cart campaigns on the storefront (docs/CART_CAMPAIGNS.md): one store per page holds the campaigns (rendered by Liquid
// from the shop metafield the app writes) and the latest cart, keeps campaign gifts in step with the cart, and renders
// <fl-cart-progress> on each surface: the bag, the cart page, the product page and the announcement bar. No request goes
// to the app; cart reads and writes go to Shopify's own cart API, like the rest of the theme.
import { cartRequest } from 'folded-label/cart-api';
import { barMessage, barModel, declinedGifts, giftChanges, GIFT_PROPERTY, liveCampaigns, productOffers, progressMessage } from 'folded-label/cart-campaigns';

const DECLINED = 'fl:declined-gifts';
const REACHED = 'fl:campaign-steps';
const session = { get(key) { try { return JSON.parse(sessionStorage.getItem(key) || '[]'); } catch { return []; } }, set(key, value) { try { sessionStorage.setItem(key, JSON.stringify(value)); } catch { /* storage unavailable */ } } };

function readData() {
  const element = document.getElementById('fl-cart-campaigns');
  if (!element) return null;
  try {
    const data = JSON.parse(element.textContent);
    // Liquid renders collection -> products; the maths wants product -> collections.
    const membership = {};
    for (const [collectionId, productIds] of Object.entries(data.collections || {})) for (const productId of productIds) (membership[String(productId)] ||= []).push(Number(collectionId));
    let payload = data.payload && data.payload.v === 1 ? data.payload : { v: 1, currency: data.currency, campaigns: [] };
    // Until a campaign gives free shipping, the theme setting's threshold (Theme settings > Cart) still drives the bar.
    const threshold = Number(data.themeFreeShipping);
    if (threshold > 0 && !liveCampaigns(payload).some(campaign => campaign.steps.some(step => step.type === 'free_shipping'))) {
      payload = { ...payload, campaigns: [...payload.campaigns, { id: 'theme', basis: 'amount', scope: { kind: 'all', productIds: [], collectionIds: [] }, startsAt: null, endsAt: null, steps: [{ key: 'shipping', threshold, type: 'free_shipping', label: 'Free shipping' }] }] };
    }
    return { payload, membership, cart: data.cart, currency: data.currency };
  } catch { return null; }
}

const rootUrl = () => { try { return JSON.parse(document.querySelector('#fl-routes').textContent).root || '/'; } catch { return '/'; } };
const publish = (name, detail) => { try { globalThis.Shopify?.analytics?.publish?.(name, detail)?.catch?.(() => undefined); } catch { /* analytics unavailable */ } };

class CampaignStore extends globalThis.EventTarget {
  constructor(data) { super(); Object.assign(this, data); this.syncing = false; this.rounds = 0; this.reached = new Set(session.get(REACHED)); }
  locale() { return document.documentElement.lang || 'en'; }
  /** A new cart from any owner (the bag, the cart page, an add): note declined gifts, re-render, then fix the gifts. */
  update(cart) {
    if (!cart?.items) return;
    const before = this.cart; this.cart = cart;
    if (before) {
      const declined = declinedGifts(before, cart, this.payload, this.membership);
      if (declined.length && !this.syncing) session.set(DECLINED, [...new Set([...session.get(DECLINED), ...declined])]);
    }
    this.track(cart);
    this.dispatchEvent(new globalThis.Event('change'));
    if (!this.syncing) { this.rounds = 0; void this.syncGifts(); }
  }
  /** Analytics: a step newly reached in this browser session, published once (consent-aware pixel, fl_campaign_step). */
  track(cart) {
    const { bar, lines } = barModel(this.payload, cart, this.membership);
    const steps = [...(bar?.steps || []), ...lines.flatMap(state => state.steps)].filter(step => step.reached && step.campaignId !== 'theme');
    let changed = false;
    for (const step of steps) {
      const key = `${step.campaignId}:${step.key}`;
      if (this.reached.has(key)) continue;
      this.reached.add(key); changed = true;
      publish('fl_campaign_step', { campaign_id: step.campaignId, step_key: step.key, step_type: step.type, label: step.label });
    }
    if (changed) session.set(REACHED, [...this.reached]);
  }
  async syncGifts() {
    const changes = giftChanges(this.payload, this.cart, this.membership, new Set(session.get(DECLINED)));
    if (!changes.add.length && !changes.remove.length && !changes.trim.length) return;
    if (this.rounds++ >= 3) return; // A cart that keeps disagreeing (a sold-out gift) is left alone.
    this.syncing = true;
    const root = rootUrl();
    try {
      for (const line of changes.remove) { await cartRequest(`${root}cart/change.js`, { method: 'POST', body: JSON.stringify({ id: line.key, quantity: 0 }) }); publish('fl_campaign_gift', { action: 'removed', gift: line.giftKey }); }
      for (const line of changes.trim) await cartRequest(`${root}cart/change.js`, { method: 'POST', body: JSON.stringify({ id: line.key, quantity: 1 }) });
      for (const gift of changes.add) {
        try { await cartRequest(`${root}cart/add.js`, { method: 'POST', body: JSON.stringify({ items: [{ id: gift.variantId, quantity: 1, properties: { [GIFT_PROPERTY]: gift.giftKey } }] }) }); publish('fl_campaign_gift', { action: 'added', gift: gift.giftKey }); }
        catch { session.set(DECLINED, [...new Set([...session.get(DECLINED), gift.giftKey])]); } // Sold out or gone: stop trying this session.
      }
      const cart = await cartRequest(`${root}cart.js`);
      window.flLatestCart = cart;
      this.syncing = false;
      document.dispatchEvent(new CustomEvent('fl:cart-updated', { detail: cart }));
    } catch { this.syncing = false; }
  }
}

let store;
function campaignStore() {
  if (store !== undefined) return store;
  const data = readData();
  store = data ? new CampaignStore(data) : null;
  if (store) {
    document.addEventListener('fl:cart-updated', event => store.update(event.detail));
    // A cart loaded before this module ran (cart.js loads first) is newer than the one Liquid drew.
    if (window.flLatestCart?.items) store.cart = window.flLatestCart;
    Promise.resolve().then(() => { if (store.cart) { store.track(store.cart); store.rounds = 0; void store.syncGifts(); } });
  }
  return store;
}

const node = (tag, className, text) => { const el = document.createElement(tag); if (className) el.className = className; if (text !== undefined) el.textContent = text; return el; };

class CartProgress extends HTMLElement {
  connectedCallback() {
    this.store = campaignStore();
    // Without campaign data the announcement keeps the header's own text; the other surfaces stay hidden.
    if (!this.store) { if (this.dataset.surface !== 'announcement') this.hidden = true; return; }
    this.render = this.render.bind(this);
    this.store.addEventListener('change', this.render);
    this.render();
  }
  disconnectedCallback() { this.store?.removeEventListener('change', this.render); }
  render() {
    const { payload, membership, currency } = this.store; const cart = this.store.cart || { items: [] }; const locale = this.store.locale();
    const surface = this.dataset.surface;
    if (surface === 'product') return this.renderProduct(productOffers(payload, cart, this.dataset.productId, membership, currency, locale));
    const model = barModel(payload, cart, membership);
    const message = barMessage(model.bar, currency, locale);
    // A product or collection deal shows in the bag once the bag holds one of its items (not as noise before).
    const lines = model.lines.filter(state => state.value > 0).map(state => progressMessage(state, currency, locale));
    if (surface === 'announcement') {
      // Liquid renders the first message (no layout shift); this keeps it current. Without a campaign message the
      // header's own announcement text shows, and without either the bar is hidden.
      const text = message?.text || lines[0]?.text || '';
      const fallback = this.querySelector('[data-fallback]');
      let live = this.querySelector('[data-campaign-text]');
      if (text) { if (!live) { live = node('span'); live.dataset.campaignText = ''; this.append(live); } live.textContent = text; if (fallback) fallback.hidden = true; }
      else { live?.remove(); if (fallback) fallback.hidden = false; }
      this.closest('.announcement')?.toggleAttribute('hidden', !text && !fallback?.textContent.trim());
      return;
    }
    // The bag and the cart page: nothing to show for an empty bag.
    this.hidden = !cart.items.length || (!model.bar && !lines.length);
    if (this.hidden) return this.replaceChildren();
    const content = [];
    if (model.bar) {
      const bar = model.bar;
      content.push(node('p', `cart-progress__message${message.reached ? ' is-reached' : ''}`, message.text));
      const track = node('div', 'cart-progress__track'); track.style.setProperty('--cart-progress', `${(bar.progress * 100).toFixed(1)}%`);
      track.setAttribute('role', 'progressbar'); track.setAttribute('aria-valuemin', '0'); track.setAttribute('aria-valuemax', String(bar.top)); track.setAttribute('aria-valuenow', String(Math.min(bar.value, bar.top))); track.setAttribute('aria-valuetext', message.text);
      // A tick on the track at each step's amount; the labels below keep the same order.
      for (const step of bar.steps) { const tick = node('span', `cart-progress__tick${step.reached ? ' is-reached' : ''}`); tick.style.setProperty('--cart-progress-at', `${(step.threshold / bar.top * 100).toFixed(1)}%`); track.append(tick); }
      const marks = node('ol', 'cart-progress__steps');
      for (const step of bar.steps) { const item = node('li', step.reached ? 'is-reached' : '', step.label); item.style.setProperty('--cart-progress-at', `${(step.threshold / bar.top * 100).toFixed(1)}%`); marks.append(item); }
      content.push(track, marks);
    }
    for (const line of lines) content.push(node('p', `cart-progress__line${line.reached ? ' is-reached' : ''}`, line.text));
    this.replaceChildren(...content);
  }
  renderProduct(offers) {
    this.hidden = !offers.length;
    this.replaceChildren(...offers.map(offer => node('p', `cart-progress__offer${offer.reached ? ' is-reached' : ''}${offer.kind === 'progress' ? ' is-progress' : ''}`, offer.text)));
  }
}
if (!customElements.get('fl-cart-progress')) customElements.define('fl-cart-progress', CartProgress);
